/**
 * Outils partagés des automatismes 4e Thalès : 1086 (calculer une longueur
 * dans deux triangles emboîtés) et 1087 (la réciproque : parallèles ou pas).
 *
 * La configuration : un sommet A, M sur [AB], N sur [AC] (triangles
 * emboîtés, cours du prof 19Thales4 p. 1 : « A, B, B' et A, C, C' sont
 * alignés »). Figure EXACTE (décision prof 24/09) : les proportions et les
 * angles sont vrais, à une échelle libre ; les LONGUEURS sont dans l'énoncé,
 * pas sur la figure (fiches 1, 6 et 7 du prof : sur deux segments posés l'un
 * sur l'autre, AM et AB, les écritures se chevaucheraient) ; les droites
 * parallèles DONNÉES sont en vert, comme sur la fiche 1 du prof (1086) ; en
 * 1087 le parallélisme est la question, tout est à l'encre.
 *
 * Longueurs en DIXIÈMES entiers (7,5 → 75) : aucun calcul flottant sur les
 * longueurs, écriture « 7,5 » dans le texte et « 7{,}5 » en KaTeX.
 */
import { pickLettresSains, tex } from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, tourner } from '../../3eme/geometrie-plane/figTriangle';

export const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');

/** Dixièmes entiers → écriture française (« 7,5 », « 6 »). */
export const frDix = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.trunc(d / 10)},${d % 10}`);
/** Dixièmes entiers → KaTeX, la virgule entre accolades (sinon KaTeX l'espace). */
export const texDix = (d) => frDix(d).replace(',', '{,}');

/** Les cinq lettres, par rôle : A le sommet, M sur [AB], N sur [AC]. */
export const tirerLettres = () => {
    const [A, M, B, N, C] = pickLettresSains(POOL, 5);
    return { A, M, B, N, C };
};

/** Les six longueurs nommées, le sommet en tête (écriture du cours : AM, AB, AN, AC, MN, BC). */
export const nomsDe = (L) => ({ AM: L.A + L.M, AB: L.A + L.B, AN: L.A + L.N, AC: L.A + L.C, MN: L.M + L.N, BC: L.B + L.C });

/** L'angle (degrés) entre les côtés x et y d'un triangle, z en face. */
export const angleEntre = (x, y, z) => (Math.acos((x * x + y * y - z * z) / (2 * x * y)) * 180) / Math.PI;

/** Les points (coordonnées abstraites) : A à l'origine, B sur l'axe, C à alpha degrés, puis miroir et rotation. */
export const pointsThales = ({ L, AB, AC, alpha, kM, kN, deg, miroir }) => {
    const a = (alpha * Math.PI) / 180;
    let base = [[0, 0], [AB, 0], [AC * Math.cos(a), AC * Math.sin(a)]];
    if (miroir) base = base.map(([x, y]) => [x, -y]);
    const [pA, pB, pC] = tourner(base, deg);
    return {
        [L.A]: pA, [L.B]: pB, [L.C]: pC,
        [L.M]: [pA[0] + kM * (pB[0] - pA[0]), pA[1] + kM * (pB[1] - pA[1])],
        [L.N]: [pA[0] + kN * (pC[0] - pA[0]), pA[1] + kN * (pC[1] - pA[1])],
    };
};

/** L'écart (degrés) entre les directions de (MN) et de (BC) : 0 si parallèles. */
export const ecartParallele = (pts, L) => {
    const u = [pts[L.N][0] - pts[L.M][0], pts[L.N][1] - pts[L.M][1]];
    const v = [pts[L.C][0] - pts[L.B][0], pts[L.C][1] - pts[L.B][1]];
    const c = Math.abs(u[0] * v[0] + u[1] * v[1]) / (Math.hypot(...u) * Math.hypot(...v));
    return (Math.acos(Math.min(1, c)) * 180) / Math.PI;
};

/**
 * La figure : les deux triangles emboîtés, (MN) et (BC) en vert quand leur
 * parallélisme est DONNÉ (paralleles), à l'encre sinon.
 */
export const figureThales = (pts, L, paralleles) => {
    const n = nomsDe(L);
    const segs = [[L.A, L.B], [L.A, L.C]];
    const deux = [[L.M, L.N], [L.B, L.C]];
    const scene = {
        points: pts, triangle: [L.A, L.B, L.C],
        segments: paralleles ? segs : [...segs, ...deux],
        vertes: paralleles ? deux : [],
    };
    const aria = `Deux triangles emboîtés de sommet ${L.A} : ${L.M} sur [${n.AB}], ${L.N} sur [${n.AC}]`
        + (paralleles ? `, les droites (${n.MN}) et (${n.BC}) en vert, parallèles.` : '.');
    return imgElement(svgTriangle(scene, aria), 'large');
};

/** La phrase de configuration (lettres en KaTeX). */
export const phraseAlignes = (L, ordre = false) => (ordre
    ? `Les points ${tex(L.A)}, ${tex(L.M)}, ${tex(L.B)} sont alignés dans cet ordre, ainsi que les points ${tex(L.A)}, ${tex(L.N)}, ${tex(L.C)}.`
    : `Les points ${tex(L.A)}, ${tex(L.M)}, ${tex(L.B)} sont alignés et les points ${tex(L.A)}, ${tex(L.N)}, ${tex(L.C)} sont alignés.`);

/** Une longueur donnée en KaTeX : « AM = 7,5 » (le « cm » suit en texte). */
export const donnee = (nom, d) => `${tex(`${nom} = ${texDix(d)}`)} cm`;

/** Un rapport en KaTeX : \dfrac{AM}{AB}. */
export const rapport = (h, b) => `\\dfrac{${h}}{${b}}`;
